import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AssessmentAnswers.css';
import API_BASE_URL from '../config';
import { 
  FiFileText, 
  FiStar, 
  FiThumbsUp, 
  FiBarChart2, 
  FiTrendingDown,
  FiRefreshCw,
  FiSearch,
  FiFilter,
  FiEye,
  FiMail,
  FiDownload,
  FiPrinter,
  FiX,
  FiClock,
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiHelpCircle,
  FiUser,
  FiCheck,
  FiAward,
  FiZap,
  FiActivity,
  FiAlertTriangle,
  FiBook,
  FiTarget,
  FiWatch,
  FiPieChart,
  FiTag,
  FiCode,
  FiMessageSquare,
  FiInfo
} from 'react-icons/fi';

const AssessmentAnswers = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [filter, setFilter] = useState('all');
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [evaluatingQuestion, setEvaluatingQuestion] = useState(null);
  const [evaluationScore, setEvaluationScore] = useState(0);
  const [evaluationFeedback, setEvaluationFeedback] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    excellent: 0,
    good: 0,
    average: 0,
    poor: 0
  });

  // Get current user from session storage
  useEffect(() => {
    const getUserFromSession = () => {
      try {
        const userDataString = sessionStorage.getItem('userData');
        
        if (userDataString) {
          const userData = JSON.parse(userDataString);
          console.log('👤 Current user for assessment answers:', userData);
          
          const userInfo = {
            email: userData.email,
            name: userData.name || userData.userName || 'User'
          };
          
          setCurrentUser(userInfo);
          console.log('✅ User set for assessment answers:', userInfo.email);
        } else {
          console.log('❌ No user data found in session storage');
        }
      } catch (error) {
        console.error('❌ Error getting user from session:', error);
      }
    };

    getUserFromSession();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadSubmissions();
    }
  }, [currentUser]);

  useEffect(() => {
    calculateStats();
  }, [submissions]);

  const calculateStats = () => {
    const stats = {
      total: submissions.length,
      excellent: submissions.filter(s => s.percentage >= 80).length,
      good: submissions.filter(s => s.percentage >= 60 && s.percentage < 80).length,
      average: submissions.filter(s => s.percentage >= 40 && s.percentage < 60).length,
      poor: submissions.filter(s => s.percentage < 40).length
    };
    setStats(stats);
  };

  const loadSubmissions = async () => {
    try {
      const token = sessionStorage.getItem('authToken');
      const userDataString = sessionStorage.getItem('userData');
      
      if (!userDataString) {
        alert('User not found. Please login again.');
        setLoading(false);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userEmail = userData.email;

      console.log('🔄 Loading submissions for user:', userEmail);
      
      const response = await axios.get(`${API_BASE_URL}/api/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          userEmail: userEmail
        }
      });

      console.log('📊 Submissions API response:', response.data);

      if (response.data.success) {
        console.log(`✅ Loaded ${response.data.data?.length || 0} submissions`);
        
        // 🔥 FIX: Ensure scores are properly set
        const submissionsWithScores = response.data.data.map(sub => ({
          ...sub,
          score: sub.score || 0,
          percentage: sub.percentage || 0,
          totalQuestions: sub.totalQuestions || 0
        }));
        
        console.log('📋 Processed submissions:', submissionsWithScores);
        setSubmissions(submissionsWithScores);
      } else {
        console.error('API returned success: false', response.data.message);
        alert('Failed to load submissions: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error loading submissions:', error);
      console.error('Error details:', error.response?.data);
      alert('Failed to load assessment submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionDetails = async (submissionId) => {
    try {
      const token = sessionStorage.getItem('authToken');
      const userDataString = sessionStorage.getItem('userData');
      
      if (!userDataString) {
        alert('User not found. Please login again.');
        return;
      }

      const userData = JSON.parse(userDataString);
      const userEmail = userData.email;

      console.log('🔄 Loading submission details for:', submissionId);

      const response = await axios.get(
        `${API_BASE_URL}/api/submissions/${submissionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            userEmail: userEmail
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Submission details loaded successfully');
        console.log('📊 Submission data:', {
          score: response.data.data.submission.score,
          percentage: response.data.data.submission.percentage,
          totalQuestions: response.data.data.submission.totalQuestions,
          evaluationStats: response.data.data.evaluationStats
        });
        
        setSelectedSubmission(response.data.data);
        setShowDetails(true);
      } else {
        alert('Failed to load submission details: ' + response.data.message);
      }
    } catch (error) {
      console.error('❌ Error loading submission details:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to view this submission.');
      } else if (error.response?.status === 404) {
        alert('Submission not found. It may have been deleted.');
      } else {
        alert('Failed to load submission details: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // 🔥 FIXED: Manual evaluation for text answers
  const evaluateTextAnswer = async (question, score, feedback) => {
    try {
      const token = sessionStorage.getItem('authToken');
      const userDataString = sessionStorage.getItem('userData');
      
      if (!userDataString) {
        alert('User not found. Please login again.');
        return;
      }

      const userData = JSON.parse(userDataString);
      const userEmail = userData.email;

      console.log('🔄 Evaluating answer for submission:', selectedSubmission.submission.submissionId);
      console.log('Question ID:', question._id);
      console.log('Score:', score, 'Feedback:', feedback);

      const response = await axios.post(
        `${API_BASE_URL}/api/submissions/${selectedSubmission.submission.submissionId}/evaluate`,
        {
          questionId: question._id,
          score: score,
          feedback: feedback,
          userEmail: userEmail
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        console.log('✅ Evaluation saved successfully');
        console.log('📊 Updated scores from backend:', response.data.data);
        
        // 🔥 FIX: Update the UI immediately with backend response
        const updatedSubmission = { ...selectedSubmission };
        
        // Update the specific question
        const questionIndex = updatedSubmission.questions.findIndex(q => q._id === question._id);
        if (questionIndex !== -1) {
          updatedSubmission.questions[questionIndex] = {
            ...updatedSubmission.questions[questionIndex],
            evaluatedScore: score,
            evaluationFeedback: feedback,
            isEvaluated: true,
            isCorrect: score > 0
          };
        }
        
        // 🔥 FIX: Update overall scores from backend
        updatedSubmission.submission.score = response.data.data.score;
        updatedSubmission.submission.percentage = response.data.data.percentage;
        updatedSubmission.submission.totalQuestions = response.data.data.totalQuestions;
        updatedSubmission.evaluationStats = response.data.data.evaluationStats;
        
        setSelectedSubmission(updatedSubmission);
        
        // 🔥 FIX: Update submissions list
        const updatedSubmissions = submissions.map(sub => {
          if (sub.submissionId === selectedSubmission.submission.submissionId) {
            return {
              ...sub,
              score: response.data.data.score,
              percentage: response.data.data.percentage,
              totalQuestions: response.data.data.totalQuestions
            };
          }
          return sub;
        });
        
        setSubmissions(updatedSubmissions);
        setEvaluatingQuestion(null);
        setEvaluationScore(0);
        setEvaluationFeedback('');
        
        // Update stats
        calculateStats();
        
        alert(`✅ Evaluation saved! Score: ${response.data.data.score}/${response.data.data.totalQuestions} (${response.data.data.percentage}%)`);
      }
    } catch (error) {
      console.error('❌ Error evaluating answer:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 404) {
        alert('Submission not found. Please refresh the page and try again.');
      } else {
        alert('Failed to save evaluation: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // Start evaluation for a text question
  const startEvaluation = (question) => {
    setEvaluatingQuestion(question);
    setEvaluationScore(question.evaluatedScore || 0);
    setEvaluationFeedback(question.evaluationFeedback || '');
  };

  // Cancel evaluation
  const cancelEvaluation = () => {
    setEvaluatingQuestion(null);
    setEvaluationScore(0);
    setEvaluationFeedback('');
  };

  // 🔥 FIXED: Search functionality
  const filteredSubmissions = submissions.filter(submission => {
    // Apply filter first
    const filterMatch = 
      filter === 'all' ||
      (filter === 'excellent' && submission.percentage >= 80) ||
      (filter === 'good' && submission.percentage >= 60 && submission.percentage < 80) ||
      (filter === 'average' && submission.percentage >= 40 && submission.percentage < 60) ||
      (filter === 'poor' && submission.percentage < 40);
    
    // Apply search if searchTerm exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const searchMatch = 
        submission.candidateName?.toLowerCase().includes(searchLower) ||
        submission.candidateEmail?.toLowerCase().includes(searchLower) ||
        submission.assessmentTitle?.toLowerCase().includes(searchLower);
      
      // Return submissions that match BOTH filter and search
      return filterMatch && searchMatch;
    }
    
    // Return submissions that match the filter only
    return filterMatch;
  });

  const formatTime = (seconds) => {
    if (!seconds || seconds === 0) return '0s';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 80) return '#10b981';
    if (percentage >= 60) return '#3b82f6';
    if (percentage >= 40) return '#f59e0b';
    return '#ef4444';
  };

  // Get answer status for display
  const getAnswerStatus = (question) => {
    if (question.type === 'multiple_choice') {
      return question.isCorrect ? 'correct' : 'incorrect';
    } else if (question.type === 'text') {
      if (question.isEvaluated) {
        return question.evaluatedScore > 0 ? 'correct' : 'incorrect';
      }
      return 'pending';
    }
    return 'pending';
  };

  // Get answer status icon
  const getAnswerStatusIcon = (question) => {
    const status = getAnswerStatus(question);
    
    switch (status) {
      case 'correct':
        return <FiCheckCircle className="text-green-500" />;
      case 'incorrect':
        return <FiXCircle className="text-red-500" />;
      case 'pending':
        return <FiClock className="text-yellow-500" />;
      default:
        return <FiHelpCircle className="text-gray-500" />;
    }
  };

  // Get answer status text
  const getAnswerStatusText = (question) => {
    const status = getAnswerStatus(question);
    
    switch (status) {
      case 'correct':
        return 'Correct Answer';
      case 'incorrect':
        return 'Incorrect Answer';
      case 'pending':
        return 'Pending Evaluation';
      default:
        return 'Not Evaluated';
    }
  };

  // 🔥 FIX: Calculate real-time score for immediate UI update
  const calculateImmediateScore = (questions) => {
    if (!questions || !questions.length) return { score: 0, total: 0, percentage: 0 };
    
    let score = 0;
    const totalQuestions = questions.length;
    
    questions.forEach(question => {
      if (question.type === 'multiple_choice' && question.isCorrect) {
        score += 1;
      } else if ((question.type === 'text' || question.type === 'coding') && question.isEvaluated) {
        score += question.evaluatedScore || 0;
      }
    });
    
    const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
    
    return { score, totalQuestions, percentage };
  };

  if (loading) {
    return (
      <div className="aa-loading-container">
        <div className="aa-loading-animation">
          <div className="aa-loading-spinner"></div>
          <div className="aa-loading-pulse"></div>
        </div>
        <p className="aa-loading-text">Loading assessment submissions...</p>
      </div>
    );
  }

  return (
    <div className="aa-main-container">
      {/* Animated Background */}
      <div className="aa-background-animation">
        <div className="aa-floating-shape aa-shape-1"></div>
        <div className="aa-floating-shape aa-shape-2"></div>
        <div className="aa-floating-shape aa-shape-3"></div>
        {/* <div className="aa-pulse-dot aa-pulse-1"></div>
        <div className="aa-pulse-dot aa-pulse-2"></div> */}
      </div>

      {/* Header Section */}
      <div className="aa-header-section">
        <div className="aa-header-content">
          <div className="aa-header-title">
            <h1 className="aa-main-title">
              <FiFileText className="aa-title-icon" />
              Assessment Analytics
            </h1>
            <p className="aa-subtitle">Track and analyze candidate assessment performance</p>
          </div>
          
          <div className="aa-header-actions">
            {currentUser && (
              <div className="aa-user-badge">
                <div className="aa-user-avatar">
                  {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="aa-user-info">
                  <span className="aa-user-name">{currentUser.name}</span>
                  <span className="aa-user-email">{currentUser.email}</span>
                </div>
              </div>
            )}
            
            <button 
              className="aa-refresh-btn"
              onClick={loadSubmissions}
            >
              <FiRefreshCw className="aa-refresh-icon" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="aa-stats-section">
        <div className="aa-stats-grid">
          <div className="aa-stat-card aa-stat-primary">
            <div className="aa-stat-icon">
              <FiFileText />
            </div>
            <div className="aa-stat-content">
              <h3 className="aa-stat-number">{stats.total}</h3>
              <p className="aa-stat-label">Total Submissions</p>
            </div>
          </div>

          <div className="aa-stat-card aa-stat-success">
            <div className="aa-stat-icon">
              <FiStar />
            </div>
            <div className="aa-stat-content">
              <h3 className="aa-stat-number">{stats.excellent}</h3>
              <p className="aa-stat-label">Excellent</p>
            </div>
          </div>

          <div className="aa-stat-card aa-stat-info">
            <div className="aa-stat-icon">
              <FiThumbsUp />
            </div>
            <div className="aa-stat-content">
              <h3 className="aa-stat-number">{stats.good}</h3>
              <p className="aa-stat-label">Good</p>
            </div>
          </div>

          <div className="aa-stat-card aa-stat-warning">
            <div className="aa-stat-icon">
              <FiBarChart2 />
            </div>
            <div className="aa-stat-content">
              <h3 className="aa-stat-number">{stats.average}</h3>
              <p className="aa-stat-label">Average</p>
            </div>
          </div>

          <div className="aa-stat-card aa-stat-danger">
            <div className="aa-stat-icon">
              <FiTrendingDown />
            </div>
            <div className="aa-stat-content">
              <h3 className="aa-stat-number">{stats.poor}</h3>
              <p className="aa-stat-label">Needs Improvement</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Section */}
      <div className="aa-controls-section">
        <div className="aa-search-filter">
          <div className="aa-search-box">
            <FiSearch className="aa-search-icon" />
            <input
              type="text"
              placeholder="Search candidates, assessments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="aa-search-input"
            />
          </div>
          
          <div className="aa-filter-tabs">
            <button 
              className={`aa-tab-btn ${filter === 'all' ? 'aa-tab-active' : ''}`}
              onClick={() => setFilter('all')}
            >
              <FiFilter className="aa-tab-icon" />
              All
            </button>
            <button 
              className={`aa-tab-btn ${filter === 'excellent' ? 'aa-tab-active' : ''}`}
              onClick={() => setFilter('excellent')}
            >
              <FiStar className="aa-tab-icon" />
              Excellent
            </button>
            <button 
              className={`aa-tab-btn ${filter === 'good' ? 'aa-tab-active' : ''}`}
              onClick={() => setFilter('good')}
            >
              <FiThumbsUp className="aa-tab-icon" />
              Good
            </button>
            <button 
              className={`aa-tab-btn ${filter === 'average' ? 'aa-tab-active' : ''}`}
              onClick={() => setFilter('average')}
            >
              <FiBarChart2 className="aa-tab-icon" />
              Average
            </button>
            <button 
              className={`aa-tab-btn ${filter === 'poor' ? 'aa-tab-active' : ''}`}
              onClick={() => setFilter('poor')}
            >
              <FiTrendingDown className="aa-tab-icon" />
              Needs Work
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {!currentUser && (
        <div className="aa-error-message">
          <FiAlertCircle className="aa-error-icon" />
          <div className="aa-error-content">
            <h4>User Session Required</h4>
            <p>Please login again to view assessment submissions</p>
          </div>
        </div>
      )}

      {/* Content Section */}
      <div className="aa-content-section">
        {currentUser && submissions.length === 0 ? (
          <div className="aa-empty-state">
            <div className="aa-empty-animation">
              <div className="aa-empty-icon">
                <FiFileText />
              </div>
              <div className="aa-empty-pulse"></div>
            </div>
            <h3 className="aa-empty-title">No Assessment Submissions Yet</h3>
            <p className="aa-empty-description">
              Candidate assessment submissions for your questions will appear here once they complete their tests.
            </p>
            <div className="aa-help-tips">
              <h4 className="aa-help-title">To Get Submissions:</h4>
              <div className="aa-tips-grid">
                <div className="aa-tip-card">
                  <FiBook className="aa-tip-icon" />
                  <div className="aa-tip-content">
                    <strong>Create Questions</strong>
                    <p>Build your question library in Questions Manager</p>
                  </div>
                </div>
                <div className="aa-tip-card">
                  <FiTarget className="aa-tip-icon" />
                  <div className="aa-tip-content">
                    <strong>Schedule Assessments</strong>
                    <p>Assign assessments to candidates</p>
                  </div>
                </div>
                <div className="aa-tip-card">
                  <FiWatch className="aa-tip-icon" />
                  <div className="aa-tip-content">
                    <strong>Candidates Complete</strong>
                    <p>Candidates take the assessments</p>
                  </div>
                </div>
                <div className="aa-tip-card">
                  <FiPieChart className="aa-tip-icon" />
                  <div className="aa-tip-content">
                    <strong>View Results</strong>
                    <p>Submissions appear here automatically</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="aa-submissions-grid">
            {filteredSubmissions.map((submission, index) => (
              <div 
                key={submission._id} 
                className="aa-submission-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="aa-card-glow"></div>
                
                <div className="aa-submission-header">
                  <div className="aa-candidate-info">
                    <div className="aa-candidate-avatar">
                      {submission.candidateName?.charAt(0)?.toUpperCase() || 'C'}
                    </div>
                    <div className="aa-candidate-details">
                      <h4 className="aa-candidate-name">{submission.candidateName}</h4>
                      <p className="aa-candidate-email">{submission.candidateEmail}</p>
                      {submission.assessmentTitle && (
                        <p className="aa-assessment-title">{submission.assessmentTitle}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* 🔥 FIXED: Score Section */}
                  <div className="aa-score-section">
                    <div 
                      className="aa-score-circle"
                      style={{ 
                        '--percentage': `${submission.percentage || 0}%`,
                        '--score-color': getScoreColor(submission.percentage || 0)
                      }}
                    >
                      <div className="aa-score-inner">
                        <span className="aa-percentage">{submission.percentage || 0}%</span>
                        <span className="aa-score">{submission.score || 0}/{submission.totalQuestions || 0}</span>
                      </div>
                    </div>
                    
                    <div className={`aa-score-badge ${
                      (submission.percentage || 0) >= 80 ? 'aa-score-excellent' : 
                      (submission.percentage || 0) >= 60 ? 'aa-score-good' : 
                      (submission.percentage || 0) >= 40 ? 'aa-score-average' : 'aa-score-poor'
                    }`}>
                      <FiAward className="inline mr-1" />
                      {(submission.percentage || 0) >= 80 ? 'Excellent' : 
                       (submission.percentage || 0) >= 60 ? 'Good' : 
                       (submission.percentage || 0) >= 40 ? 'Average' : 'Needs Work'}
                    </div>
                  </div>
                </div>

                <div className="aa-submission-details">
                  <div className="aa-details-grid">
                    <div className="aa-detail-item">
                      <FiClock className="aa-detail-icon" />
                      <div className="aa-detail-content">
                        <label>Time Spent</label>
                        <span>{formatTime(submission.timeSpent)}</span>
                      </div>
                    </div>
                    
                    <div className="aa-detail-item">
                      <FiCalendar className="aa-detail-icon" />
                      <div className="aa-detail-content">
                        <label>Submitted</label>
                        <span>{new Date(submission.submittedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    
                    <div className="aa-detail-item">
                      <FiActivity className="aa-detail-icon" />
                      <div className="aa-detail-content">
                        <label>Status</label>
                        <span className={`aa-status aa-status-${submission.status}`}>
                          {submission.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="aa-submission-actions">
                  <button 
                    className="aa-view-details-btn"
                    onClick={() => loadSubmissionDetails(submission.submissionId)}
                  >
                    <FiEye className="aa-btn-icon" />
                    View Details
                  </button>
                  
                  <button 
                    className="aa-email-btn"
                    onClick={() => window.open(`mailto:${submission.candidateEmail}`, '_blank')}
                  >
                    <FiMail className="aa-btn-icon" />
                    Email
                  </button>

                  {/* <button 
                    className="aa-download-btn"
                    onClick={() => {
                      alert('Download feature coming soon!');
                    }}
                  >
                    <FiDownload className="aa-btn-icon" />
                    Report
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detailed Answers Modal */}
      {showDetails && selectedSubmission && (
        <div className="aa-modal-overlay">
          <div className="aa-modal-content">
            <div className="aa-modal-header">
              <div className="aa-modal-title-section">
                <h2 className="aa-modal-title">Assessment Results</h2>
                {currentUser && (
                  <div className="aa-user-indicator">
                    <FiUser className="inline mr-1" />
                    Assessment Owner: <strong>{currentUser.email}</strong>
                  </div>
                )}
              </div>
              <button 
                className="aa-close-btn" 
                onClick={() => {
                  setShowDetails(false);
                  setSelectedSubmission(null);
                }}
              >
                <FiX />
              </button>
            </div>

            <div className="aa-modal-body">
              {/* Candidate Summary */}
              <div className="aa-candidate-summary">
                <div className="aa-candidate-header">
                  <div className="aa-candidate-avatar-large">
                    {selectedSubmission.submission.candidateName?.charAt(0)?.toUpperCase() || 'C'}
                  </div>
                  <div className="aa-candidate-info-large">
                    <h3>{selectedSubmission.submission.candidateName}</h3>
                    <p className="aa-candidate-email-large">{selectedSubmission.submission.candidateEmail}</p>
                    <p className="aa-submission-date">
                      <FiCalendar className="inline mr-1" />
                      Submitted: {new Date(selectedSubmission.submission.submittedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="aa-summary-scores">
                  <div className="aa-summary-score-card">
                    <div className="aa-score-value">
                      {selectedSubmission.submission.score || 0}/{selectedSubmission.submission.totalQuestions || 0}
                      {selectedSubmission.evaluationStats?.pendingEvaluation > 0 && (
                        <span className="aa-pending-indicator">
                          <FiAlertCircle className="inline mr-1" />
                          (+{selectedSubmission.evaluationStats.pendingEvaluation} pending)
                        </span>
                      )}
                    </div>
                    <div className="aa-score-label">Questions Correct</div>
                  </div>
                  <div className="aa-summary-score-card">
                    <div 
                      className="aa-percentage-value"
                      style={{ color: getScoreColor(selectedSubmission.submission.percentage || 0) }}
                    >
                      {selectedSubmission.submission.percentage || 0}%
                      {selectedSubmission.evaluationStats?.pendingEvaluation > 0 && (
                        <span className="aa-tentative-indicator">
                          <FiAlertTriangle className="inline mr-1" />
                        </span>
                      )}
                    </div>
                    <div className="aa-score-label">Overall Score</div>
                  </div>
                  <div className="aa-summary-score-card">
                    <div className="aa-time-value">
                      <FiClock className="inline mr-1" />
                      {formatTime(selectedSubmission.submission.timeSpent)}
                    </div>
                    <div className="aa-score-label">Time Spent</div>
                  </div>
                </div>

                {selectedSubmission.evaluationStats && (
                  <div className="aa-evaluation-progress">
                    <div className="aa-progress-bar">
                      <div 
                        className="aa-progress-fill"
                        style={{ 
                          width: `${selectedSubmission.evaluationStats.completionRate || 0}%` 
                        }}
                      ></div>
                    </div>
                    <div className="aa-progress-text">
                      <FiZap className="inline mr-1" />
                      Evaluation Progress: {selectedSubmission.evaluationStats.evaluatedQuestions || 0}/
                      {selectedSubmission.evaluationStats.totalQuestions || 0} questions evaluated
                      ({selectedSubmission.evaluationStats.completionRate || 0}%)
                    </div>
                  </div>
                )}
              </div>

              {/* Questions & Answers */}
              <div className="aa-questions-section">
                <h4 className="aa-section-title">
                  <FiFileText className="inline mr-2" />
                  Questions & Answers
                </h4>
                
                {selectedSubmission.questions && selectedSubmission.questions.map((question, index) => (
                  <div key={question._id || index} className="aa-question-card">
                    <div className="aa-question-header">
                      <div className="aa-question-meta">
                        <h5 className="aa-question-number">Question {index + 1}</h5>
                        <div className="aa-question-tags">
                          <span className="aa-question-type">
                            {question.type === 'coding' ? <FiCode className="inline mr-1" /> : 
                             question.type === 'text' ? <FiMessageSquare className="inline mr-1" /> : 
                             <FiHelpCircle className="inline mr-1" />}
                            {question.type}
                          </span>
                          {question.difficulty && (
                            <span className={`aa-difficulty aa-difficulty-${question.difficulty.toLowerCase()}`}>
                              {question.difficulty}
                            </span>
                          )}
                          {question.category && (
                            <span className="aa-category-tag">
                              <FiTag className="inline mr-1" />
                              {question.category}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className={`aa-answer-status-indicator aa-answer-status-${getAnswerStatus(question)}`}>
                        <span className="aa-status-icon">{getAnswerStatusIcon(question)}</span>
                        <span className="aa-status-text">{getAnswerStatusText(question)}</span>
                      </div>
                    </div>
                    
                    <div className="aa-question-content">
                      <p className="aa-question-text">{question.question}</p>
                    </div>

                    <div className="aa-answer-section">
                      <h6 className="aa-answer-title">Candidate's Answer:</h6>
                      {renderAnswerDisplay(question, question.candidateAnswer || selectedSubmission.submission.answers?.[question._id])}
                    </div>

                    {question.type === 'multiple_choice' && question.options && (
                      <div className="aa-options-section">
                        <h6 className="aa-options-title">
                          Options:
                        </h6>
                        <div className="aa-options-grid">
                          {question.options.map((option, optIndex) => (
                            <div 
                              key={optIndex} 
                              className={`aa-option-item ${
                                option === question.correctAnswer ? 'aa-option-correct' : ''
                              } ${
                                option === (question.candidateAnswer || selectedSubmission.submission.answers?.[question._id]) ? 'aa-option-selected' : ''
                              }`}
                            >
                              <span className="aa-option-letter">{String.fromCharCode(65 + optIndex)}</span>
                              <span className="aa-option-text">{option}</span>
                              {option === question.correctAnswer && (
                                <span className="aa-correct-indicator">
                                  <FiCheck className="inline mr-1" />
                                  Correct Answer
                                </span>
                              )}
                              {option === (question.candidateAnswer || selectedSubmission.submission.answers?.[question._id]) && 
                               option !== question.correctAnswer && (
                                <span className="aa-incorrect-indicator">
                                  <FiX className="inline mr-1" />
                                  Candidate's Choice
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {question.type === 'text' && (
                      <div className="aa-evaluation-section">
                        {evaluatingQuestion && evaluatingQuestion._id === question._id ? (
                          <div className="aa-evaluation-form">
                            <h6 className="aa-evaluation-title">
                              <FiCheckCircle className="inline mr-1" />
                              Evaluate Answer
                            </h6>
                            <div className="aa-evaluation-score">
                              <label>Score:</label>
                              <div className="aa-score-options">
                                <button 
                                  className={`aa-score-option ${evaluationScore === 1 ? 'aa-score-selected' : ''}`}
                                  onClick={() => setEvaluationScore(1)}
                                >
                                  <FiCheck className="inline mr-1" />
                                  Correct (1 point)
                                </button>
                                <button 
                                  className={`aa-score-option ${evaluationScore === 0 ? 'aa-score-selected' : ''}`}
                                  onClick={() => setEvaluationScore(0)}
                                >
                                  <FiX className="inline mr-1" />
                                  Incorrect (0 points)
                                </button>
                              </div>
                            </div>
                            <div className="aa-evaluation-feedback">
                              <label>
                                <FiMessageSquare className="inline mr-1" />
                                Feedback:
                              </label>
                              <textarea
                                value={evaluationFeedback}
                                onChange={(e) => setEvaluationFeedback(e.target.value)}
                                placeholder="Provide feedback to the candidate..."
                                rows="3"
                                className="aa-feedback-textarea"
                              />
                            </div>
                            <div className="aa-evaluation-actions">
                              <button 
                                className="aa-save-evaluation-btn"
                                onClick={() => evaluateTextAnswer(question, evaluationScore, evaluationFeedback)}
                              >
                                <FiCheck className="inline mr-1" />
                                Save Evaluation
                              </button>
                              <button 
                                className="aa-cancel-evaluation-btn"
                                onClick={cancelEvaluation}
                              >
                                <FiX className="inline mr-1" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="aa-evaluation-panel">
                            {question.isEvaluated ? (
                              <div className="aa-evaluation-result">
                                <div className="aa-evaluation-score-display">
                                  <FiCheckCircle className="inline mr-1 text-green-500" />
                                  <strong>Score: </strong>
                                  {question.evaluatedScore === 1 ? 'Correct' : 'Incorrect'}
                                </div>
                                {question.evaluationFeedback && (
                                  <div className="aa-evaluation-feedback-display">
                                    <FiMessageSquare className="inline mr-1 text-blue-500" />
                                    <strong>Feedback: </strong>
                                    {question.evaluationFeedback}
                                  </div>
                                )}
                                <button 
                                  className="aa-reevaluate-btn"
                                  onClick={() => startEvaluation(question)}
                                >
                                  <FiRefreshCw className="inline mr-1" />
                                  Re-evaluate
                                </button>
                              </div>
                            ) : (
                              <div className="aa-evaluation-prompt">
                                <p>
                                  <FiAlertCircle className="inline mr-1 text-yellow-500" />
                                  This text answer needs manual evaluation
                                </p>
                                <button 
                                  className="aa-evaluate-btn"
                                  onClick={() => startEvaluation(question)}
                                >
                                  <FiCheckCircle className="inline mr-1" />
                                  Evaluate Answer
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {question.explanation && (
                      <div className="aa-explanation-section">
                        <h6 className="aa-explanation-title">
                          <FiInfo className="inline mr-1" />
                          Explanation:
                        </h6>
                        <p className="aa-explanation-text">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="aa-modal-actions">
              <button 
                className="aa-print-btn"
                onClick={() => window.print()}
              >
                <FiPrinter className="aa-btn-icon" />
                Print Results
              </button>
              <button 
                className="aa-close-modal-btn"
                onClick={() => {
                  setShowDetails(false);
                  setSelectedSubmission(null);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to render different answer types
const renderAnswerDisplay = (question, answer) => {
  if (!answer) return <span className="aa-no-answer">No answer provided</span>;
  
  switch (question.type) {
    case 'multiple_choice':
      return <p className="aa-answer-text">{answer}</p>;
    
    case 'coding':
      return (
        <pre className="aa-code-answer">{answer}</pre>
      );
    
    case 'text':
      return <p className="aa-text-answer">{answer}</p>;
    
    default:
      return <p>{answer}</p>;
  }
};

export default AssessmentAnswers;